import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, CircularProgress } from "@mui/material";
import PropTypes from "prop-types";
import selectors from "selectors";
import { fetchPatientNotifications } from "actions/notifications";
import NotificationItem from "./notification-item";
import useTranslate from "hooks/use-translate-language";
import { setPatientLastBrowse } from "reducers/notifications";
import moment from "moment";
import "./notification.scss";
import { getBrowserTimezone } from "utils/misc";

const NotificationsModal = ({ isOpen, onClose }) => {
  const translate = useTranslate();
  const dispatch = useDispatch();
  const notifications = useSelector(selectors.getPatientNotifications);
  const timezone = getBrowserTimezone()
  const [isFetching, setIsFetching] = useState(false);
  const [snapshotNewNotificationsAmount, setSnapshotNewNotificationsAmount] = useState(0);
  
  const newNotificationsAmount = useSelector(selectors.getNewNotificationsAmount);
  
  const hasCapturedSnapshot = useRef(false);

  useEffect(() => {
    setIsFetching(true);
    dispatch(fetchPatientNotifications()).then(() => {
      setIsFetching(false);
    });
  }, [dispatch]);

  useEffect(() => {
    //only set newNotificationsAmount in first time opening the modal
    if (isOpen && !hasCapturedSnapshot.current) {
      setSnapshotNewNotificationsAmount(newNotificationsAmount);
      hasCapturedSnapshot.current = true;
      
      dispatch(setPatientLastBrowse(moment().tz(timezone).format("YYYY-MM-DD HH:mm:ss")));
    }
    
    if (!isOpen) {
      hasCapturedSnapshot.current = false;
    }
  }, [isOpen, dispatch, timezone, newNotificationsAmount]);

  const decreaseAmountOfNewNotifications = () => {
    setSnapshotNewNotificationsAmount(prev => Math.max(0, prev - 1));
  };

  return (
    <Dialog
      id="notifications-dialog"
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-label={translate("notifications")}
      //align to right
      PaperProps={{
        sx: {
          maxHeight: "80vh",
          position: "absolute",
          top: "80px",
          right: "10%",
          margin: 0,
          overflowY: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        },
      }}
    >
      <div className="notifications-modal-container">
        <div className="notifications-modal-content">
          {isFetching ? (
            <div className="notifications-modal-loading">
              <CircularProgress size={30} />
            </div>
          ) : notifications.length === 0 ? (
            <div className="notifications-modal-empty">
              <p>{translate("noNotifications")}</p>
            </div>
          ) : (
            notifications.map((notification, i) => (
              <NotificationItem
                key={notification.notification_id}
                {...notification}
                isNew={i < snapshotNewNotificationsAmount}
                decreaseAmountOfNewNotifications={decreaseAmountOfNewNotifications}
              />
            ))
          )}
        </div>
      </div>
    </Dialog>
  );
};

NotificationsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default NotificationsModal;
